
































































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import {
  AccessPointPermissionName,
  AccessPointDefaultFilterTableInterface,
  AccessPointDefaultTableItemInterface,
  AccessPointInterface,
  AccessPointFormModelInterface,
  Status,
} from '@access-control/interfaces/access-point';
import TableWrapper from '@shared/layouts/wrappers/table/table-wrapper.vue';
import PaginationInterface from '@core/interfaces/pagination-interface';
import OrderingInterface from '@core/interfaces/ordering-interface';
import { SelectOptionInterface } from '@shared/interfaces/inputs';
import AccessPointFilters from '@access-control/components/tables/access-point/access-point-filters.vue';
import TableOrdering from '@shared/components/tables/table-ordering/table-ordering.vue';
import { BPCTableField } from '@shared/interfaces/bpc-table-interface';
import {
  accessPointDisplayFields,
  defaultTableFields,
} from '@access-control/config/access_point_options';
import Table from '@shared/components/tables/table.vue';
import DisplayFieldCode from '@shared/components/displays/display-field-code/display-field-code.vue';
import DisplayFieldBadge from '@shared/components/displays/display-field-badge/display-field-badge.vue';
import DisplayFieldArray from '@shared/components/displays/display-field-array/display-field-array.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import AccessPointForm from '@access-control/components/forms/access-point/access-point-form.vue';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';

@Component({
  name: 'bc-access-point-table',
  components: {
    'bpc-table-wrapper': TableWrapper,
    'bc-access-point-filters': AccessPointFilters,
    'bc-table-ordering': TableOrdering,
    'bpc-table': Table,
    'bc-display-code': DisplayFieldCode,
    'bc-display-field-badge': DisplayFieldBadge,
    'bc-display-field-array': DisplayFieldArray,
    'bc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bc-confirmation-modal': ConfirmationModal,
    'bpc-drawer-modal': DrawerModal,
    'bc-access-point-form': AccessPointForm,
  },
})
export default class AccessPointTable extends Vue {
  declare $refs: {
    softDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    hardDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    recoverConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    accessPointForm: InstanceType<typeof AccessPointForm> & {
      saveEntity(): void;
    };
  };

  // PROPS
  @Prop({ type: String }) readonly actionsPortalTarget: string;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop({ type: Boolean, default: true }) readonly showFilters: boolean;
  @Prop({ type: Boolean, default: true }) readonly showOrdering: boolean;
  @Prop({ default: () => defaultTableFields }) readonly tableFields!: BPCTableField[];
  @Prop({ type: Boolean, default: true }) readonly withUrlParams: boolean;
  @Prop({ type: Boolean, default: false }) readonly noCheckboxColumn: boolean;
  @Prop({ type: Object }) readonly initialFilters: AccessPointDefaultFilterTableInterface;

  // DATA
  // pagination
  pagination: PaginationInterface = {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    from: 0,
    to: 0,
    total: 0,
  };
  pageOptions: number[] = [5, 10, 15, 20, 50, 100, 200];

  // filters
  filters: AccessPointDefaultFilterTableInterface = {
    search: null,
    resource_ids: [],
    status: [],
  };

  // Ordering
  ordering: OrderingInterface = {
    order_by: 'code',
    order_direction: 'asc',
  };
  // Ordering options
  orderByOptions: SelectOptionInterface[] = [
    {
      text: this.$t('global.ordering.code.label').toString(),
      value: 'code',
    },
  ];

  tableItems: AccessPointDefaultTableItemInterface[] = [];
  checkedAllEntities: boolean = false;
  loading: boolean = false;

  // Create update data
  isSavingForm: boolean = false;
  showAccessPointForm: boolean = false;
  // Initalize form model
  accessPointFormModel: AccessPointFormModelInterface = {
    id: null,
    code: null,
    center_id: this.tenantId,
    name: '',
    description: '',
    status: Status.Active,
    consumeProduct: false,
    resourceAccessConfigurations: [],
  };

  // LIFECYCLE

  mounted() {
    this.initializeFiltersFromProperties();
    this.getAccessPointsIfStopChanging();
  }

  // COMPUTED

  get tenantId() {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  get permissionEntityName(): string {
    return AccessPointPermissionName;
  }

  get showCreatePageAction(): boolean {
    return (
      !this.disabled &&
      this.$authorizationService.hasPermissionForAction(this.permissionEntityName, 'store')
    );
  }

  get fieldsToShow(): object[] {
    return this.tableFields.sort((f1, f2) => f1.order - f2.order);
  }

  // Table column config
  get displayConfig() {
    return accessPointDisplayFields;
  }

  // Change detector
  get changeDetectorStr(): string {
    return [
      this.pagination?.per_page,
      this.pagination?.current_page,
      this.ordering?.order_by,
      this.ordering?.order_direction,
      this.filters?.search,
      this.filters?.resource_ids,
      this.filters?.status,
    ].join('--');
  }

  // Soft delete action
  get showDeleteRowAction(): boolean {
    return (
      !this.disabled &&
      this.$authorizationService.hasPermissionForAction(this.permissionEntityName, 'delete')
    );
  }

  get softDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'access-point-soft-delete-modal',
      title: this.$t('access_control.access_point.modals.soft_delete_title') as string,
      body: this.$t('access_control.access_point.modals.soft_delete_body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.move_to_trash') as string,
      acceptButtonVariant: 'danger',
    };
  }

  get deleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'access-point-delete-modal',
      title: this.$t('access_control.access_point.modals.delete_title') as string,
      body: this.$t('access_control.access_point.modals.delete_body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }

  get showRecoverRowAction(): boolean {
    return (
      !this.disabled &&
      this.$authorizationService.hasPermissionForAction(this.permissionEntityName, 'recover')
    );
  }

  get recoverConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'access-point-recover-modal',
      title: this.$t('access_control.access_point.modals.recover_title') as string,
      body: this.$t('access_control.access_point.modals.recover_body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.recover') as string,
      acceptButtonVariant: 'info',
    };
  }

  //  Edit action

  get showEditRowAction(): boolean {
    return (
      !this.disabled &&
      this.$authorizationService.hasPermissionForAction(this.permissionEntityName, 'update')
    );
  }

  get accessPointId() {
    return this.accessPointFormModel?.id;
  }

  get formModalTitle() {
    if (this.accessPointId) {
      return this.$t('access_control.access_point.modals.edit_title');
    } else {
      return this.$t('access_control.access_point.modals.create_title');
    }
  }

  // METHODS

  // Method called from the refresh button at filter level.
  clickOnReload() {
    this.getAccessPointsIfStopChanging();
  }
  // Method called from the order direction button
  clickOnChangeOrderDirection(direction: string) {
    this.ordering.order_direction = direction;
  }
  // Method called when selecting all rows of the table
  onCheckAll(val: boolean) {
    this.checkedAllEntities = val;
  }

  clickOnEdit(item: Partial<AccessPointInterface>) {
    console.log('JJFC', item);
    this.accessPointFormModel.id = item.id;
    this.showAccessPointForm = true;
  }

  clickOnDelete(item: AccessPointInterface) {
    this.$refs.softDeleteConfirmationModal.onOpenModal(item.id);
  }

  clickOnHardDelete(item: AccessPointInterface) {
    this.$refs.hardDeleteConfirmationModal.onOpenModal(item.id);
  }

  clickOnRecover(item) {
    this.$refs.recoverConfirmationModal.onOpenModal(item.id);
  }

  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.getAccessPointsIfStopChanging();
  }

  onSuccessDelete() {
    this.$refs.hardDeleteConfirmationModal.onCloseModal();
    this.getAccessPointsIfStopChanging();
  }

  onSuccessRecover() {
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.$refs.recoverConfirmationModal.onCloseModal();
    this.getAccessPointsIfStopChanging();
  }

  // Params sents in the fetch request
  getQueryParams() {
    return {
      order_by: this.ordering.order_by,
      order_direction: this.ordering.order_direction,
      per_page: this.pagination?.per_page,
      page: this.pagination?.current_page,
      search: this.filters.search,
      resource_ids: this.filters.resource_ids,
      status: this.filters.status,
    };
  }

  setUrlParams(params) {
    if (this.withUrlParams) {
      this.$router.push({ query: params }).catch(e => {
        return null;
      });
    }
  }

  async getAccessPoints() {
    this.loading = true;
    this.checkedAllEntities = false;
    const params = this.getQueryParams();
    const url = this.$bpcApiRouteHelper.getEntityPath('access-points');
    const res = await this.$bpcApi.get(url, { params: params }).toPromise();
    this.tableItems = res.data;
    this.pagination = res.meta;
    this.setUrlParams(params);
    this.loading = false;
  }

  //get access points with a delay after changes detected
  getAccessPointsIfStopChanging() {
    const changeStr = this.changeDetectorStr;
    setTimeout(() => {
      if (changeStr === this.changeDetectorStr) {
        this.getAccessPoints();
      }
    }, 300);
  }

  // Display modal
  clickOnCreate() {
    this.showAccessPointForm = true;
  }
  // Reset FormModel
  resetAccessPointDFormModel() {
    this.accessPointFormModel = {
      id: null,
      code: null,
      center_id: this.tenantId,
      name: '',
      description: '',
      status: Status.Active,
      consumeProduct: null,
      resourceAccessConfigurations: [],
    };
  }

  clickOnCloseForm() {
    this.showAccessPointForm = false;
    this.isSavingForm = false;
    this.resetAccessPointDFormModel();
  }

  clickOnSaveForm() {
    this.isSavingForm = true;
    this.$refs.accessPointForm.saveEntity();
  }

  onSavedForm() {
    this.getAccessPointsIfStopChanging();
    this.clickOnCloseForm();
  }

  onErrorSavingForm() {
    this.isSavingForm = false;
  }

  initializeFiltersFromProperties() {
    if (this.initialFilters) {
      this.filters.search = this.initialFilters?.search || null;
      if (this.initialFilters?.resource_ids) {
        if (Array.isArray(this.initialFilters?.resource_ids)) {
          this.filters.resource_ids = this.initialFilters?.resource_ids;
        } else {
          this.filters.resource_ids = [this.initialFilters?.resource_ids];
        }
      } else {
        this.filters.resource_ids = [];
      }
    }
  }

  // WATCHERS
  @Watch('changeDetectorStr')
  onChangeDetectorStrChange(val: string) {
    if (val) {
      this.getAccessPointsIfStopChanging();
    }
  }
}
